import { Box, Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Spinner } from './helpers/Spinner';
import { OmniumTable } from './tables/OmniumTable';
import { RelatedEvents } from './helpers/RelatedEvents';
import { ResultsTable } from './tables/ResultsTable';
import { parseBlocks } from '../utils/parseBlocks';
import { TableDoubles } from './tables/TableDoubles';
import { SprintTable } from './tables/SprintTable';
import { NoDisciplineData } from './helpers/NoDisciplineData';

// Helper type definitions
interface Discipline {
    name: string;
    sheetName?: string;
    rowRange: [number, number];
    columns: string[];
    relatedEvents?: string[];
    displayName?: string;
    boldRows?: number[];
}

interface EventDay {
    day: number;
    date: string;
    events: Discipline[];
}

interface EventData {
    name: string;
    googleSheetsId: string;
    disciplines: EventDay[];
}

export const DisciplineLive: FC = () => {
    const { eventName, disciplineName } = useParams<{ eventName: string; disciplineName: string }>();
    const navigate = useNavigate();

    // State
    const [eventDisplayName, setEventDisplayName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [data, setData] = useState<any[][]>([]);
    const [sheetName, setSheetName] = useState('');
    const [relatedEvents, setRelatedEvents] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSheetLoading, setIsSheetLoading] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Load event metadata
    useEffect(() => {
        if (!eventName || !disciplineName) return;

        setIsLoading(true);

        fetch(`/api/events/${eventName}`)
            .then((res) => res.json())
            .then((jsonData: EventData) => {
                setEventDisplayName(jsonData.name);

                const discipline = jsonData.disciplines
                    .flatMap((day) => day.events)
                    .find(
                        (d) =>
                            d.name.replace(/\s+/g, '-').replace(/\//g, '-').toLowerCase() ===
                            disciplineName.toLowerCase(),
                    );

                if (discipline) {
                    setSheetName(discipline.sheetName || '');
                    setRelatedEvents(discipline.relatedEvents || []);
                    setDisplayName(discipline.displayName || discipline.name);
                    const selectedIndex = discipline.relatedEvents?.findIndex(
                        (item) => item === discipline.displayName || item === discipline.name,
                    );
                    setSelectedIndex(selectedIndex || 0);
                }
            })
            .finally(() => setIsLoading(false))
            .catch((err) => console.error('❌ Error loading event JSON:', err));
    }, [eventName, disciplineName]);

    // Load Google Sheets data - UPRAVENÁ VERZE SE ZÁCHRANOU
    useEffect(() => {
        if (!eventName || !disciplineName) return;

        setIsSheetLoading(true);
        
        // Vytvoříme unikátní název pro tuto disciplínu, aby se data nepletla
        const cacheKey = `cache_${eventName}_${disciplineName}`;

        const fetchData = () => {
            fetch(`/api/sheets/${eventName}/${disciplineName}`)
                .then((res) => {
                    // Pokud Google vrátí chybu (např. limit 429), jdeme do catch bloku
                    if (!res.ok) throw new Error('API limit reached');
                    return res.json();
                })
                .then((jsonData: { values: any[][] }) => {
                    if (jsonData.values && jsonData.values.length > 0) {
                        // Vše v pořádku -> uložíme do paměti a zobrazíme
                        setData(jsonData.values);
                        localStorage.setItem(cacheKey, JSON.stringify(jsonData.values));
                    }
                })
                .catch((err) => {
                    console.warn('⚠️ Výpadek Google Sheets. Tahám data z lokální paměti.', err);
                    
                    // Zkusíme najít v prohlížeči poslední verzi pro tuto disciplínu
                    const savedData = localStorage.getItem(cacheKey);
                    if (savedData) {
                        setData(JSON.parse(savedData));
                    }
                })
                .finally(() => setIsSheetLoading(false));
        };

        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [eventName, disciplineName]);

    const parsed = useMemo(() => parseBlocks(data), [data]);

    if (isLoading || isSheetLoading) {
        return <Spinner />;
    }

    const isOmnium =
        data?.[0]?.some((h) => h.trim().toLowerCase() === 'om. points') &&
        data?.[0]?.some((h) => h.trim().toLowerCase() === 'points race');
    const isSprint = data?.[0]?.some((h) => h.trim().toLowerCase() === 'decider');
    const isMadison = sheetName?.toLowerCase()?.includes('madison');

    return (
        <>
            <Box sx={{ width: isMobile ? '90%' : '85%', mx: 'auto', mt: 4 }}>
                {/* 🔙 Back Button */}
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/event/${eventName}`)} sx={{ mb: 2 }}>
                    Back to {eventDisplayName || eventName?.replace(/-/g, ' ')}
                </Button>

                {/* 🏁 Discipline Header */}
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
                    <Typography variant="h4" fontWeight={700}>
                        {displayName}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
                        Results
                    </Typography>
                </Paper>

                {/* 🔀 Related Events */}
                {relatedEvents.length > 0 && (
                    <RelatedEvents
                        relatedEvents={relatedEvents}
                        selectedIndex={selectedIndex}
                        setSelectedIndex={setSelectedIndex}
                        eventName={eventName || ''}
                    />
                )}

                {/* 📊 Data Table / Component */}
                {data.length === 0 && <NoDisciplineData />}

                {parsed?.map((block, index) => {
                    if (block.data.length === 1) return null;

                    if (isOmnium) {
                        return <OmniumTable values={block.data} key={index} />;
                    } else if (isMadison) {
                        return <TableDoubles values={block.data} key={index} />;
                    } else if (isSprint) {
                        return <SprintTable values={block.data} title={block.tableTitle} key={index} />;
                    } else {
                        return <ResultsTable values={block.data} title={block.tableTitle} key={index} />;
                    }
                })}
            </Box>
        </>
    );
};
